/**
 * One-shot reveal modal for a freshly-minted webhook signing secret.
 *
 * Mirrors ``ApiKeyRevealModal`` (PR-4b): the plaintext lives only in
 * component state; on ``Done`` the modal unmounts and the value is
 * GC'd. Never placed in localStorage, the URL, or any axios payload
 * after the original create / rotate. There is no recovery path — a
 * lost secret means re-rotation.
 *
 * The operator pastes this secret into their webhook handler's
 * ``X-Senti-Signature`` HMAC verification (same wire format as the
 * inbound API signing, ADR-0004 / ADR-0008).
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";

export interface WebhookSecretRevealModalProps {
  /** The full plaintext signing secret returned by the server. */
  secret: string;
  /** The endpoint URL — surfaced for context. */
  url: string;
  onDone: () => void;
}

export function WebhookSecretRevealModal({
  secret,
  url,
  onDone,
}: WebhookSecretRevealModalProps) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);

  async function copyToClipboard() {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(secret);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      } catch {
        // Silent — operator can still read + retype the secret.
      }
    }
  }

  // Non-dismissable: no overlay click, no Escape close.
  return (
    <div
      role="alertdialog"
      aria-labelledby="webhook-secret-title"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div className="w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
        <h2 id="webhook-secret-title" className="text-lg font-semibold">
          Signing secret — copy it now
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          This secret is shown <strong>exactly once</strong>. Paste it into
          your webhook handler's <code className="font-mono">X-Senti-Signature</code>{" "}
          verification before closing — it cannot be reconstructed from the
          database, only re-rotated.
        </p>

        <div className="mt-4">
          <label
            htmlFor="webhook-secret-value"
            className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
          >
            Signing secret
          </label>
          <div className="mt-1 flex items-stretch gap-2">
            <code
              id="webhook-secret-value"
              data-testid="webhook-secret-value"
              className="flex-1 select-all rounded-md border bg-muted/30 p-3 font-mono text-sm break-all"
            >
              {secret}
            </code>
            <Button
              type="button"
              variant="outline"
              onClick={copyToClipboard}
              aria-label="Copy signing secret to clipboard"
            >
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>

        <p className="mt-3 text-xs text-muted-foreground break-all">
          Endpoint: <code className="font-mono">{url}</code>
        </p>

        <label className="mt-6 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            data-testid="webhook-secret-ack"
          />
          <span>
            I have copied this secret to a secure location (secrets manager,
            env file, etc.).
          </span>
        </label>

        <div className="mt-6 flex justify-end">
          <Button
            type="button"
            disabled={!acknowledged}
            onClick={onDone}
            data-testid="webhook-secret-done"
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}

export default WebhookSecretRevealModal;
